import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { analytics } from '../services/analytics'
import './SurveyPage.css'

type SurveyRating = 'excellent' | 'good' | 'fair' | 'poor'

interface SurveyResponse {
  rating: SurveyRating
  timestamp: number
  sessionId?: string
}

const SurveyPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const preSelectedRating = searchParams.get('rating') as SurveyRating | null

  useEffect(() => {
    // If rating is pre-selected from URL, auto-submit
    if (preSelectedRating && !submitted) {
      handleSurveySubmit(preSelectedRating)
    }
  }, [preSelectedRating, submitted])

  const handleSurveySubmit = async (rating: SurveyRating) => {
    if (isSubmitting || submitted) return

    setIsSubmitting(true)

    try {
      // Create survey response object
      const surveyResponse: SurveyResponse = {
        rating,
        timestamp: Date.now(),
        sessionId: sessionStorage.getItem('audioGuideSessionId') || undefined
      }

      // TODO: Send to Firebase Functions API
      // For now, store in localStorage for demonstration
      const existingResponses = JSON.parse(localStorage.getItem('surveyResponses') || '[]')
      existingResponses.push(surveyResponse)
      localStorage.setItem('surveyResponses', JSON.stringify(existingResponses))

      console.log('Survey submitted:', surveyResponse)

      // Analytics: Track survey completion
      analytics.trackSurveyCompleted(rating)

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000))

      setSubmitted(true)

      // Navigate to completion page after brief delay
      setTimeout(() => {
        navigate('/completion')
      }, 2000)

    } catch (error) {
      console.error('Survey submission failed:', error)
      // Even if submission fails, proceed to completion
      navigate('/completion')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getRatingColor = (rating: SurveyRating): string => {
    switch (rating) {
      case 'excellent': return '#4CAF50'
      case 'good': return '#8BC34A'
      case 'fair': return '#FF9800'
      case 'poor': return '#F44336'
      default: return '#666'
    }
  }

  const getRatingEmoji = (rating: SurveyRating): string => {
    switch (rating) {
      case 'excellent': return '😍'
      case 'good': return '😊'
      case 'fair': return '😐'
      case 'poor': return '😞'
      default: return '🙂'
    }
  }

  if (submitted) {
    return (
      <div className="survey-page submitted-state">
        <div className="background-container">
          <img 
            src="/hep-five-primary-image.jpg" 
            alt="HEP-FIVE View"
            className="background-image"
          />
          <div className="background-overlay"></div>
        </div>

        <div className="survey-content">
          <div className="thank-you-message">
            <div className="success-icon">✅</div>
            <h2>Thank You!</h2>
            <p>Your feedback helps us improve the experience for future visitors.</p>
            <div className="loading-message">
              <span>Redirecting to completion...</span>
              <div className="loading-dots">
                <span>.</span>
                <span>.</span>
                <span>.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="survey-page">
      <div className="background-container">
        <img 
          src="/hep-five-primary-image.jpg" 
          alt="HEP-FIVE View"
          className="background-image"
        />
        <div className="background-overlay"></div>
      </div>

      <div className="survey-content">
        <div className="survey-header">
          <h1>How was your experience?</h1>
          <p>Help us improve the HEP-FIVE audio guide for future visitors</p>
        </div>

        <div className="rating-grid">
          {(['excellent', 'good', 'fair', 'poor'] as SurveyRating[]).map((rating) => (
            <button
              key={rating}
              className={`rating-button ${rating}`}
              onClick={() => handleSurveySubmit(rating)}
              disabled={isSubmitting}
              style={{ 
                background: isSubmitting ? '#ccc' : getRatingColor(rating),
                cursor: isSubmitting ? 'not-allowed' : 'pointer'
              }}
            >
              <div className="rating-emoji">{getRatingEmoji(rating)}</div>
              <div className="rating-text">{rating.charAt(0).toUpperCase() + rating.slice(1)}</div>
            </button>
          ))}
        </div>

        {isSubmitting && (
          <div className="submitting-message">
            <span>Submitting your feedback...</span>
            <div className="loading-spinner">⏳</div>
          </div>
        )}

        <div className="survey-footer">
          <button 
            className="skip-button"
            onClick={() => navigate('/completion')}
            disabled={isSubmitting}
          >
            Skip Survey
          </button>
          
          <div className="privacy-note">
            <small>Your response is anonymous and helps improve our service</small>
          </div>
        </div>

      </div>
    </div>
  )
}

export default SurveyPage